export type PaginationMeta = {
  count: number;
  currentPage: number;
  totalPages: number;
  next: string | null;
  previous: string | null;
};

export type PaginatedResult<T> = {
  items: T[];
  pagination: PaginationMeta;
};

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function toInt(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

/** Pull the array out of a paginated / wrapped / bare list payload. */
export function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  const obj = payload as Record<string, unknown> | null;
  if (obj && typeof obj === "object") {
    if (Array.isArray(obj.data)) {
      return obj.data as T[];
    }
    if (Array.isArray(obj.results)) {
      return obj.results as T[];
    }
    const nested = obj.data as Record<string, unknown> | undefined;
    if (nested && Array.isArray(nested.results)) {
      return nested.results as T[];
    }
  }
  return [];
}

/**
 * Read DRF-style pagination metadata from a response envelope.
 * Falls back to a single-page result when the envelope is absent.
 */
export function extractPaginationMeta(
  payload: unknown,
  itemCount: number,
  requestedPage = 1
): PaginationMeta {
  const obj = payload as Record<string, unknown> | null;
  const p = obj?.pagination as Record<string, unknown> | undefined;
  if (p && typeof p === "object") {
    const count = toInt(p.count, itemCount);
    const currentPage = toInt(p.current_page, requestedPage);
    const totalPages = toInt(p.total_pages, 1);
    return {
      count,
      currentPage,
      totalPages,
      next: (p.next as string | null) ?? null,
      previous: (p.previous as string | null) ?? null,
    };
  }
  return {
    count: itemCount,
    currentPage: requestedPage,
    totalPages: 1,
    next: null,
    previous: null,
  };
}

/** Build page / page_size query params (omitted when not provided). */
export function buildPageParams(
  page?: number,
  pageSize?: number
): Record<string, string> {
  const params: Record<string, string> = {};
  if (page && page > 0) {
    params.page = String(page);
  }
  if (pageSize && pageSize > 0) {
    params.page_size = String(pageSize);
  }
  return params;
}
